import React from 'react';
import { ArrowRight, CheckCircle, Shield, AlertTriangle, Users, TrendingUp, FileText } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const EAACompliancePage = () => {
    const complianceServices = [
        {
            icon: <FileText className="w-6 h-6" />,
            title: "EAA Compliance Audit",
            description: "Comprehensive assessment of your digital services against European Accessibility Act requirements.",
            features: ["Complete WCAG 2.1 AA evaluation", "Legal risk assessment", "Detailed compliance report", "Remediation roadmap"]
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Accessibility Implementation",
            description: "Full remediation service to bring your website and applications into EAA compliance.",
            features: ["Critical issue resolution", "Accessibility coding", "Testing & validation", "Compliance certification"]
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Staff Training & Support",
            description: "Comprehensive training programmes to ensure your team maintains accessibility standards.",
            features: ["Developer training", "Content creator guidance", "Accessibility processes", "Ongoing consultancy"]
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "Ongoing Monitoring",
            description: "Continuous compliance monitoring to ensure your digital services remain EAA compliant.",
            features: ["Automated testing", "Regular audits", "Compliance reporting", "Issue alerts"]
        }
    ];

    const urgentBenefits = [
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Legal Protection",
            description: "Avoid EAA penalties ranging from €10,000 to €100,000+ and protect your EU market access."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Market Expansion",
            description: "Reach 101 million disabled consumers in the EU with €13 trillion combined purchasing power."
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "Competitive Advantage",
            description: "95% of EU websites currently fail accessibility checks - be in the compliant 5%."
        },
        {
            icon: <CheckCircle className="w-6 h-6" />,
            title: "Enhanced Performance",
            description: "Accessible websites typically see 12% higher search rankings and 25% improved user satisfaction."
        }
    ];

    const affectedServices = [
        {
            category: "E-commerce Platforms",
            description: "Online shops selling to EU consumers",
            requirements: [
                "Accessible product catalogues",
                "Inclusive checkout processes",
                "Screen reader compatible interfaces",
                "Keyboard navigation support",
                "Clear form labelling",
                "Alternative payment methods"
            ]
        },
        {
            category: "Banking & Financial Services",
            description: "Digital banking and financial platforms",
            requirements: [
                "Accessible account management",
                "Secure authentication flows",
                "Transaction accessibility",
                "Document accessibility",
                "Mobile app compliance",
                "Customer support features"
            ]
        },
        {
            category: "Transport & Travel",
            description: "Booking and travel management systems",
            requirements: [
                "Accessible booking flows",
                "Journey planning tools",
                "Ticket management systems",
                "Mobile check-in processes",
                "Real-time information",
                "Customer communication"
            ]
        },
        {
            category: "Digital Services",
            description: "Software and digital platforms",
            requirements: [
                "User interface accessibility",
                "Content management systems",
                "Communication platforms",
                "Document sharing",
                "Video conferencing",
                "Customer portals"
            ]
        }
    ];

    const complianceProcess = [
        {
            step: "01",
            title: "Initial Assessment",
            description: "Rapid evaluation to determine if your business falls under EAA requirements and identify immediate risks.",
            duration: "24-48 hours"
        },
        {
            step: "02",
            title: "Comprehensive Audit",
            description: "Detailed technical audit using WCAG 2.1 AA standards with automated and manual testing.",
            duration: "3-5 days"
        },
        {
            step: "03",
            title: "Risk Analysis",
            description: "Legal and business impact assessment with prioritised remediation recommendations.",
            duration: "2 days"
        },
        {
            step: "04",
            title: "Implementation Plan",
            description: "Phased approach to achieve compliance with timelines, costs, and resource requirements.",
            duration: "1 day"
        },
        {
            step: "05",
            title: "Remediation",
            description: "Technical implementation of accessibility improvements across all digital touchpoints.",
            duration: "2-8 weeks"
        },
        {
            step: "06",
            title: "Validation & Certification",
            description: "Final testing and compliance certification to demonstrate EAA conformance.",
            duration: "1 week"
        }
    ];

    const urgencyIndicators = [
        "EAA enforcement began 28 June 2025 - penalties are active",
        "95% of EU websites currently fail basic accessibility checks",
        "Average penalties range from €10,000 to €100,000 per violation",
        "Non-compliant businesses risk losing EU market access",
        "Implementation typically takes 4-8 weeks for full compliance",
        "Early adopters gain significant competitive advantages"
    ];

    const exemptions = [
        {
            title: "Micro-enterprise Exemption",
            description: "Companies with fewer than 10 employees AND annual turnover below €2 million",
            note: "Both criteria must be met"
        },
        {
            title: "Disproportionate Burden",
            description: "Where compliance would fundamentally alter the nature of the service or create financial hardship",
            note: "Requires formal assessment and documentation"
        },
        {
            title: "Legacy Products",
            description: "Products placed on market before 28 June 2025 may have transitional periods",
            note: "Up to 5 years for certain categories"
        }
    ];

    return (
        <Layout
            title="European Accessibility Act (EAA) Compliance Services"
            description="EAA compliance services for UK businesses selling to EU consumers. Expert accessibility audits, implementation, and certification to meet European Accessibility Act requirements."
            keywords="European Accessibility Act, EAA compliance UK, WCAG 2.1 AA, accessibility audit, EU accessibility requirements, digital accessibility services"
            path="/services/eaa-compliance"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-red-50 to-orange-50">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-full text-sm font-medium mb-8">
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    URGENT: EAA Enforcement Active
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    European Accessibility Act
                                    <span className="block text-red-600 mt-2">Compliance Services</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    The EAA deadline has passed. UK businesses serving EU customers must comply immediately 
                                    or face penalties up to €100,000+. Get compliant fast with our expert accessibility 
                                    services and protect your European market access.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/services/eaa-compliance-scanner" className="bg-green-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-green-700 transition-all hover:shadow-lg flex items-center justify-center">
                                        Free Website Scanner <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    <Link to="/contact" className="bg-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-700 transition-all hover:shadow-lg flex items-center justify-center">
                                        Get Emergency Audit <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    {/* <Link to="/schedule-consultation" className="border-2 border-red-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-red-300 hover:bg-red-50 transition-all text-center">
                                        Free Risk Assessment
                                    </Link> */}
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-red-200">
                                    <div className="text-center mb-6">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-2">Compliance Status</h3>
                                        <p className="text-slate-600">Current EU website accessibility</p>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Fully Compliant</span>
                                            <span className="text-green-600 font-semibold">5%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Non-Compliant</span>
                                            <span className="text-red-600 font-semibold">95%</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Average Penalties</span>
                                            <span className="text-slate-900 font-semibold">€10K-€100K+</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Implementation Time</span>
                                            <span className="text-slate-900 font-semibold">4-8 Weeks</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Urgency Section */}
                <section className="py-16 bg-red-600 text-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold mb-4">Why This Is Urgent</h2>
                            <p className="text-xl text-red-100 max-w-3xl mx-auto">
                                The European Accessibility Act enforcement began 28 June 2025. Penalties are active now.
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {urgencyIndicators.map((indicator, index) => (
                                <div key={index} className="flex items-start">
                                    <AlertTriangle className="w-5 h-5 text-red-200 mr-3 flex-shrink-0 mt-1" />
                                    <span className="text-red-100">{indicator}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">EAA Compliance Services</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive solutions to achieve and maintain European Accessibility Act compliance
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {complianceServices.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="grid grid-cols-1 gap-3">
                                        {service.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why EAA Compliance Matters</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Beyond legal requirements, accessibility opens new markets and improves business performance
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {urgentBenefits.map((benefit, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {benefit.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{benefit.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Affected Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Services Covered by EAA</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Key digital services that must comply with European Accessibility Act requirements
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {affectedServices.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.category}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="space-y-3">
                                        {service.requirements.map((requirement, requirementIndex) => (
                                            <div key={requirementIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{requirement}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Compliance Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Systematic approach to achieve EAA compliance with minimal business disruption
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {complianceProcess.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center mb-4">
                                            <h3 className="text-2xl font-bold text-slate-900 mr-4">{step.title}</h3>
                                            <span className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm font-medium">
                                                {step.duration}
                                            </span>
                                        </div>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Exemptions Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">EAA Exemptions</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Limited exemptions are available, but most UK businesses selling to EU customers must comply
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-8">
                            {exemptions.map((exemption, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{exemption.title}</h3>
                                    <p className="text-slate-600 mb-4 leading-relaxed">{exemption.description}</p>
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <p className="text-amber-800 text-sm font-medium">{exemption.note}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Investment & Timeline</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Transparent pricing with rapid implementation to get you compliant quickly
                            </p>
                        </div>
                        
                        <div className="grid lg:grid-cols-3 gap-8">
                            <div className="bg-white rounded-2xl p-8 border border-slate-200">
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">Emergency Audit</h3>
                                <div className="text-4xl font-bold text-slate-900 mb-2">£250</div>
                                <p className="text-slate-600 mb-6">Rapid compliance assessment</p>
                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">24-48 hour delivery</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">EAA applicability assessment</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Critical issues identified</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Priority action plan</span>
                                    </li>
                                </ul>
                                <Link to="/contact" className="block bg-slate-900 text-white text-center py-3 rounded-xl font-semibold hover:bg-slate-800 transition-all">
                                    Get Emergency Audit
                                </Link>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border-2 border-red-500 relative">
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                                    Most Popular
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">Full Compliance</h3>
                                <div className="text-4xl font-bold text-slate-900 mb-2">£2,500 - £8,000</div>
                                <p className="text-slate-600 mb-6">Complete EAA implementation</p>
                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Comprehensive audit included</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Full remediation service</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">WCAG 2.1 AA compliance</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Testing & validation</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Compliance certification</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">4-8 week delivery</span>
                                    </li>
                                </ul>
                                <Link to="/contact" className="block bg-red-500 text-white text-center py-3 rounded-xl font-semibold hover:bg-red-600 transition-all">
                                    Start Implementation
                                </Link>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200">
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">Ongoing Support</h3>
                                <div className="text-4xl font-bold text-slate-900 mb-2">£500 - £1,500</div>
                                <p className="text-slate-600 mb-6">Monthly monitoring & support</p>
                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Automated monitoring</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Quarterly compliance reports</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Issue resolution support</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Staff training updates</span>
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                        <span className="text-slate-700">Regulatory updates</span>
                                    </li>
                                </ul>
                                <Link to="/contact" className="block bg-slate-900 text-white text-center py-3 rounded-xl font-semibold hover:bg-slate-800 transition-all">
                                    Discuss Support
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ROI Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">Return on Investment</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    EAA compliance protects your EU market access and opens new opportunities. 
                                    The cost of compliance is minimal compared to potential penalties and lost revenue.
                                </p>
                                
                                <div className="space-y-6">
                                    <div className="flex items-start">
                                        <Shield className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="text-lg font-semibold text-slate-900 mb-2">Penalty Avoidance</h4>
                                            <p className="text-slate-600">Avoid fines ranging from €10,000 to €100,000+ per violation</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <Users className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="text-lg font-semibold text-slate-900 mb-2">Market Access</h4>
                                            <p className="text-slate-600">Protect your EU revenue streams and customer relationships</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <TrendingUp className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="text-lg font-semibold text-slate-900 mb-2">New Customers</h4>
                                            <p className="text-slate-600">Reach 101 million disabled consumers in the EU market</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="text-lg font-semibold text-slate-900 mb-2">SEO Benefits</h4>
                                            <p className="text-slate-600">Accessible sites typically rank 12% higher in search results</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Cost-Benefit Analysis</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                        <span className="text-slate-600">Average Implementation</span>
                                        <span className="text-slate-900 font-semibold">£2,500 - £8,000</span>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                        <span className="text-slate-600">Potential Penalties (avoided)</span>
                                        <span className="text-green-600 font-semibold">€10,000 - €100,000+</span>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                        <span className="text-slate-600">EU Market Protection</span>
                                        <span className="text-green-600 font-semibold">Existing Revenue</span>
                                    </div>
                                    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                        <span className="text-slate-600">New Market Access</span>
                                        <span className="text-green-600 font-semibold">15-25% Growth</span>
                                    </div>
                                    <div className="flex items-center justify-between pt-4">
                                        <span className="text-lg font-semibold text-slate-900">ROI Timeline</span>
                                        <span className="text-lg font-bold text-green-600">Immediate</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-slate-900 text-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold mb-6">EAA Compliance Success Metrics</h2>
                            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                Proven results from our accessibility compliance implementations
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-4 gap-8 text-center">
                            <div>
                                <div className="text-4xl font-bold mb-2">100%</div>
                                <div className="text-slate-300">Compliance Rate</div>
                            </div>
                            <div>
                                <div className="text-4xl font-bold mb-2">4-8 Weeks</div>
                                <div className="text-slate-300">Implementation</div>
                            </div>
                            <div>
                                <div className="text-4xl font-bold mb-2">€0</div>
                                <div className="text-slate-300">Penalties Incurred</div>
                            </div>
                            <div>
                                <div className="text-4xl font-bold mb-2">24/7</div>
                                <div className="text-slate-300">Support</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default EAACompliancePage;